import config from "config.json";
import useFetch from "hooks/useFetch";
import useLocalStorage from "hooks/useLocalStorage";
import useScript from "hooks/useScript";
import useSessionStorage from "hooks/useSessionStorage";
import { useEffect, useRef, useState } from "react";
import {
	addCurrentRedirectUrl,
	getCdn,
	getEnvironment,
	isAllMarketingSubscribed,
	isCdnPage,
	isCurrentPathPresent,
	isDevelopmentEnvironment,
	isIsolatedEnvironment,
	isTravelAgent,
	mapString,
	mapStringWithObject,
	objectIsEmpty,
	validateJsonWebToken,
} from "utils";
import { loadAffiliationsFromCookies } from "utils/affiliations";
import { getCookie, removeCookie } from "utils/cookie";
import { buildHref } from "utils/link";

const isolatedEnvironment = isIsolatedEnvironment();

const maxChecks = 60;
const timeoutMs = 500;
const asyncMessagingTokenKey = "navigationAppAsyncMessagingValue";
const authenticationEvent = "navigationAuth";
const defaultUserType = "GUEST";
const launchLoginMethod = "launchLogin";
const loadHeavyNotificationsKey = "navigationAppLoadHeavyNotifications";
const loadHeavyNotificationsReloadTime = 1800000; // 30 minutes
const navigationPublishEvent = "navigation";
const notificationsCookieName = "notificationCount";
const oneIdPrefix = "oneId-";
const syndicatedDismissButton = "syndicatedDismissButton";
const syndicatedMakeCartRequest = "syndicatedMakeUserCartRequest";
const authenticatorBundlePath = "/authenticator/authenticator-bundle.js";
let navigationAllowReloadAndRedirect = false;
let localOneIdLibraryInstance = isolatedEnvironment ? null : undefined;

const publishNavigationEvent = (eventName, data = {}) => {
	document?.dispatchEvent(
		new CustomEvent(navigationPublishEvent, {
			detail: {
				event: eventName,
				data,
			},
		})
	);
};

const publishOneIdError = (error) => {
	publishNavigationEvent(`${oneIdPrefix}error`, error);
};

const getTravelAgentDataFromCookie = () => {
	const ticketDataJarCookie = getCookie("TICKET_DATA_jar");
	if (ticketDataJarCookie) {
		try {
			const travelAgentCookieData = JSON.parse(
				decodeURIComponent(ticketDataJarCookie)
			);
			if (
				travelAgentCookieData?.firstName ||
				travelAgentCookieData?.partnerId
			) {
				const {
					city,
					country,
					partnerId,
					partnerIdType,
					phone,
					addressLine1: line1,
					addressLine2: line2,
					agencyName: name,
					email: emailAddress,
					state: stateOrProvince,
					zipCode: postalCode,
					...rest
				} = travelAgentCookieData;
				return {
					emailAddress,
					travelAgency: {
						name,
						partnerId,
						partnerIdType,
						phone,
						address: {
							line1,
							line2,
							city,
							stateOrProvince,
							postalCode,
							country,
						},
					},
					...rest,
				};
			}
		} catch (error) {
			console.log(error);
		}
	}
	return null;
};

const getNotificationsType = (brandConfig = {}, appEnv) => {
	let notificationsType = "default";
	if (brandConfig?.loadHeavyNotifications) {
		notificationsType = "heavy";
	}
	if (brandConfig?.useNotificationsCountEnvironments?.includes(appEnv)) {
		notificationsType = "count";
	}
	return notificationsType;
};

const useProfile = (
	brand,
	locale,
	profile,
	store,
	affiliationsParam,
	chromeData,
	basePath,
	additionalProfileInfo,
	bannerProfile,
	disableProfileLibrary,
	isConversationStarted,
	closeChat
) => {
	const getOneIdConfig = () => {
		if (!config.skipOneId) {
			const oneIdConfig = {
				...config.profile.oneId,
				...brandConfig?.oneId,
				...config.environmentConfiguration?.default?.oneId,
				...config.environmentConfiguration?.[appEnv]?.oneId,
				...(config.environmentConfiguration?.[appEnv]?.oneId
					?.brandConfigurations?.[brand] || {}),
			};
			const oneIdEnv = oneIdConfig?.oneIdEnv;
			let usingOneIdLibrary = oneIdConfig?.usingOneIdLibrary
				? oneIdConfig.usingOneIdLibrary.includes(brand)
				: false;
			let usingOneIdProfileLibrary = oneIdConfig?.usingOneIdProfileLibrary
				? oneIdConfig.usingOneIdProfileLibrary.includes(brand)
				: false;
			const forceScriptLoadAndInitialization = isCurrentPathPresent(
				oneIdConfig.forceScriptLoadAndInitializationPages || []
			);
			if (usingOneIdLibrary || usingOneIdProfileLibrary) {
				if (localOneIdLibraryInstance === undefined) {
					localOneIdLibraryInstance = null;
					if (usingOneIdLibrary) {
						if (
							(oneIdConfig.preventScriptLoad ||
								(disableProfileLibrary !== undefined &&
									disableProfileLibrary)) &&
							!forceScriptLoadAndInitialization
						) {
							localOneIdLibraryInstance = true;
						} else {
							localOneIdLibraryInstance = window?.OneID;
						}
					} else if (usingOneIdProfileLibrary && window?.PEP?.PROFILE_AUTH) {
						localOneIdLibraryInstance = window?.PEP?.PROFILE_AUTH;
					}
				}
				if (oneIdConfig.brandConfigurations?.[brand]?.includedPages) {
					const currentIsIncludedPage = isCurrentPathPresent(
						oneIdConfig.brandConfigurations?.[brand]?.includedPages
					);
					if (!currentIsIncludedPage) {
						usingOneIdLibrary = false;
						usingOneIdProfileLibrary = false;
					}
				} else if (oneIdConfig.brandConfigurations?.[brand]?.excludedPages) {
					const currentIsExcludedPage = isCurrentPathPresent(
						oneIdConfig.brandConfigurations?.[brand]?.excludedPages
					);
					usingOneIdLibrary = usingOneIdLibrary && !currentIsExcludedPage;
					usingOneIdProfileLibrary =
						usingOneIdProfileLibrary && !currentIsExcludedPage;
				}
				if (
					oneIdConfig.brandConfigurations?.[brand]
						?.excludedFromPreventInitializationPages
				) {
					if (
						isCurrentPathPresent(
							oneIdConfig.brandConfigurations?.[brand]
								?.excludedFromPreventInitializationPages
						)
					) {
						oneIdConfig.preventInitialization = false;
					}
				}
			}
			const isOneIdSource = usingOneIdLibrary || usingOneIdProfileLibrary;
			const oneIdClientId = isOneIdSource
				? mapString(oneIdConfig?.clientId, oneIdEnv)
				: "";
			const responderPath = oneIdConfig?.responderPagePath;
			const responderVersion = oneIdConfig?.responderVersion;
			const initializeOneId = () => {
				let oneIdProfileLibraryUrl;
				let oneIdResponderPage;
				if (oneIdConfig?.oneIdHostSupport && !isDevelopmentEnvironment()) {
					if (
						/(localhost)/i.test(window?.location.host) &&
						oneIdConfig?.localTestingAuthDomain
					) {
						oneIdProfileLibraryUrl = `${oneIdConfig.localTestingAuthDomain}${authenticatorBundlePath}`;
					} else {
						oneIdProfileLibraryUrl = usingOneIdProfileLibrary
							? "https://" + window?.location.host + authenticatorBundlePath
							: "";
					}
				} else {
					oneIdProfileLibraryUrl = usingOneIdProfileLibrary
						? mapString(
								oneIdConfig?.oneIdProfileLibrary,
								oneIdConfig?.oneIdProfileLibraryUrlPrefix
						  )
						: "";
				}
				oneIdResponderPage = isOneIdSource
					? mapStringWithObject(
							isCdnPage()
								? `${window?.location.pathname.replace("/index.html", "")}${
										config.environmentConfiguration.development?.oneId
											?.responderPage
								  }`
								: oneIdConfig?.responderPage,
							{
								responderVersion,
								oneIdEnv,
								oneIdClientId,
								origin: window?.location.origin,
								responderPath,
							}
					  )
					: "";
				return {
					oneIdProfileLibraryUrl,
					oneIdResponderPage,
				};
			};
			let shouldInitializeOneId = () =>
				!oneIdConfig?.disableForCookies?.some((cookie) => getCookie(cookie));
			let loadOneId =
				oneIdConfig?.enableForStore?.includes(store) || shouldInitializeOneId();
			// initializeOneId is only called once
			let oneIdProfileLibraryUrl;
			let oneIdResponderPage;
			const init = loadOneId ? initializeOneId() : null;
			oneIdProfileLibraryUrl = init?.oneIdProfileLibraryUrl;
			oneIdResponderPage = init?.oneIdResponderPage;
			const oneIdLibraryUrl = isOneIdSource
				? mapString(
						oneIdConfig?.oneIdLibrary,
						oneIdConfig?.oneIdLibraryUrlPrefix
				  )
				: "";
			const disneyIDUrl = isOneIdSource
				? usingOneIdProfileLibrary
					? oneIdProfileLibraryUrl
					: oneIdLibraryUrl
				: "";
			return {
				...oneIdConfig,
				forceScriptLoadAndInitialization,
				usingOneIdProfileLibrary,
				usingOneIdLibrary,
				isOneIdSource,
				oneIdClientId,
				oneIdResponderPage,
				oneIdLibraryUrl,
				disneyIDUrl,
			};
		}
	};

	const resetOnActionRedirection = () => {
		if (disneyIdRef.current?.onLoginRedirectTo) {
			disneyIdRef.current.onLoginRedirectTo = null;
		}
		if (disneyIdRef.current?.onLogoutRedirectTo) {
			disneyIdRef.current.onLogoutRedirectTo = null;
		}
	};

	const appEnv = isCdnPage() ? "development" : getEnvironment();
	const brandConfig = config.brands?.[brand];
	const notificationsType = getNotificationsType(brandConfig, appEnv);
	const oneIdConfig = getOneIdConfig();
	const isOneIdSource = oneIdConfig?.isOneIdSource || false;

	const [user, setUser] = useState(null);
	const [profileUrl, setProfileUrl] = useState("");
	const [openedOneIdModal, setOpenedOneIdModal] = useState(false);
	const [isLoading, setIsLoading] = useState(profile && isOneIdSource);
	const [affiliations, setAffiliations] = useState([]);
	const [userType, setUserType] = useState(
		brandConfig?.isUserTypeRequiredForContentRequest &&
			window?.location.pathname.split("/").includes("trade-public")
			? "TRAVEL_AGENT_NOT_SIGNED_IN"
			: null
	);
	const [travelAgent, setTravelAgent] = useState(null);
	const [showSubscriptionMessage, setShowSubscriptionMessage] = useState(false);
	const [, setStorageMakeCartRequest] = useSessionStorage(
		syndicatedMakeCartRequest,
		true
	);
	const [, setSyndicatedDismissButton] = useLocalStorage(
		syndicatedDismissButton,
		false
	);
	const [asyncMessagingToken, setAsyncMessagingToken] = useSessionStorage(
		asyncMessagingTokenKey,
		null
	);
	const [loadHeavyNotificationsObject, setLoadHeavyNotificationsObject] =
		useSessionStorage(loadHeavyNotificationsKey, null);

	const disneyIdRef = useRef();
	const isConversationStartedRef = useRef(isConversationStarted);
	const closeChatRef = useRef(closeChat);
	const status = useScript(
		disableProfileLibrary !== undefined &&
			!disableProfileLibrary &&
			profile &&
			isOneIdSource &&
			!localOneIdLibraryInstance
			? oneIdConfig?.disneyIDUrl
			: null,
		"head"
	);
	const [notificationCount, setNotificationCount] = useState(
		getCookie(notificationsCookieName)
	);
	const [triggerNotificationCount, setTriggerNotificationCount] = useState(0);

	const { response, error } = useFetch(
		profileUrl,
		{
			headers: { swid: user?.swid || "", token: user?.authToken || "" },
		},
		0,
		profileUrl
	);

	const setNewAffiliations = (newAffiliations = []) => {
		setAffiliations((currentAffiliations = []) => {
			return Array.from(new Set([...currentAffiliations, ...newAffiliations]));
		});
	};

	const readDataFromCookie = () => {
		const travelAgentData = getTravelAgentDataFromCookie();
		if (travelAgentData) {
			setUserType("TRAVEL_AGENT");
			setTravelAgent(travelAgentData);
		} else if (!userType && brandConfig?.isUserTypeRequiredForContentRequest) {
			setUserType(defaultUserType);
		}
	};

	useEffect(() => {
		setNewAffiliations(loadAffiliationsFromCookies(brand));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (affiliationsParam?.length) {
			setNewAffiliations(affiliationsParam);
		}
	}, [affiliationsParam]);

	useEffect(() => {
		if (
			profile &&
			isOneIdSource &&
			!disneyIdRef.current &&
			(status === "ready" || localOneIdLibraryInstance)
		) {
			initOneId();
		}
		if (status === "error") {
			publishOneIdError({ message: `Unable to load script url` });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, localOneIdLibraryInstance]);

	useEffect(() => {
		if (response) {
			!userType && setUserType(defaultUserType);
			const affiliationsCookieKey =
				config.cookies.affiliations[config.cookies.affiliations.length - 1];
			const geolocationCookieKey =
				config.cookies.geolocation[config.cookies.geolocation.length - 1];
			if (response[affiliationsCookieKey] || response[geolocationCookieKey]) {
				setNewAffiliations(
					loadAffiliationsFromCookies(
						brand,
						response[affiliationsCookieKey],
						response[geolocationCookieKey]
					)
				);
			}

			if (!objectIsEmpty(response.user) && response.user.firstName) {
				const firstName = response.user.firstName;
				const lastName = response.user.lastName;
				const email = response.user.email;
				const membership = response.user.membership;
				const cookieBasedNotifications =
					notificationsType !== "count" &&
					!isNaN(notificationCount) &&
					notificationCount > 0;
				if (window) {
					window.navigationGuestIdentifiers =
						response.user?.guestIdentifiers || null;
					window.document.dispatchEvent(
						new CustomEvent("navigationGuestIdentifiers", {
							detail: window?.navigationGuestIdentifiers,
						})
					);
				}
				if (response.user?.affiliations?.length) {
					setNewAffiliations(response.user.affiliations);
				}
				if (response.user.asyncMessagingToken) {
					setAsyncMessagingToken(response.user.asyncMessagingToken);
				}
				if (notificationsType === "heavy") {
					if (profileUrl.includes("loadHeavyNotifications=true")) {
						if (response.user?.notificationsResponse && !notificationCount) {
							const notificationObjectToStore = {
								messages: getNotifications(
									response.user.notificationsResponse,
									false
								),
								itemCount: response.user.notificationsResponse?.itemCount,
								updatedAt: new Date().getTime(),
								outdated: false,
							};
							setLoadHeavyNotificationsObject(notificationObjectToStore);
							setNotificationCount(
								response.user?.notificationsResponse?.itemCount
							);
						} else if (loadHeavyNotificationsObject) {
							setNotificationCount(loadHeavyNotificationsObject.itemCount);
						}
					}
				}

				setUser({
					...user,
					firstName,
					lastName,
					email: user?.email || response.user.email,
					avatar: response.user.avatar,
					affiliations: response.user.affiliations,
					messages: getNotifications(response.user.notificationsResponse, true),
					ebsMessageBanner: response.user.messageBanner,
					membership,
					memberClubId: response.user.memberClubId,
					cookieBasedNotifications,
					registrationDate:
						response.user.profileAggregatedResponse?.registrationDate,
					authenticatedChatToken:
						response.user.asyncMessagingToken || asyncMessagingToken,
					isDataLoaded: true,
				});
				publishNavigationEvent("userInformation", {
					swid: user?.swid,
					firstName,
					lastName,
					email,
					telephone: response.user.telephone || "",
					title: response.user.title || "",
					country: response.user.country || user?.region || "",
				});
			} else {
				setUser(null);
			}
			if (response.travelAgent?.travelAgency) {
				setTravelAgent(response.travelAgent);
				response.travelAgent?.userType &&
					setUserType(response.travelAgent.userType);
			} else {
				readDataFromCookie();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		if (error) {
			if (!userType) {
				setUserType(defaultUserType);
			}
			if (user) {
				setUser({
					...user,
					isDataLoaded: true,
					avatar: {
						...user?.avatar,
						url: `${getCdn()}${config.profile.defaultAvatarUrl}`,
					},
				});
			}
			readDataFromCookie();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		const exportAuthLogic = (event) => {
			resetOnActionRedirection();
			const {
				method,
				onLoginRedirectTo = null,
				onLogoutRedirectTo = null,
				preventReloadAndRedirect = false,
				preventExecuteOneIdMethod = false,
			} = event.detail;
			switch (method) {
				case launchLoginMethod:
					handleLogin(
						event,
						onLoginRedirectTo,
						onLogoutRedirectTo,
						preventReloadAndRedirect,
						preventExecuteOneIdMethod
					);
					break;
				case "launchReauth":
					if (isOneIdSource) {
						handleClick(
							method,
							onLoginRedirectTo,
							onLogoutRedirectTo,
							preventReloadAndRedirect,
							preventExecuteOneIdMethod
						)(event);
					}
					break;
				case "launchProfile":
					handleProfile(event, onLoginRedirectTo, onLogoutRedirectTo, true);
					break;
				case "handleReadNotification":
					handleReadNotification();
					break;
				case "logout":
					handleLogout(
						event,
						onLogoutRedirectTo,
						true,
						preventReloadAndRedirect,
						preventExecuteOneIdMethod
					);
					break;
				case "setLoggedInStatus":
					if (onLogoutRedirectTo) {
						if (!disneyIdRef.current) {
							disneyIdRef.current = {};
						}
						disneyIdRef.current.onLogoutRedirectTo = onLogoutRedirectTo;
					}
					break;
				case "handleSubscription":
					handleSubscription(event);
					break;
				default:
					if (isOneIdSource) {
						handleClick(method)(event);
					}
			}
		};

		document?.addEventListener(authenticationEvent, exportAuthLogic);

		if (!isLoading && chromeData) {
			publishNavigationEvent(`${authenticationEvent}Listening`);
		}

		return () =>
			document?.removeEventListener(authenticationEvent, exportAuthLogic);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, chromeData, user]);

	useEffect(() => {
		const travelAgentHeader = getCookie("SHARED_SESSION_LINK");
		const makeProfileRequestForGettingTAData = travelAgentHeader && store;
		if (
			(user?.swid ||
				brandConfig?.makeProfileRequest ||
				makeProfileRequestForGettingTAData) &&
			!brandConfig?.preventProfileRequest
		) {
			const apiUrl = config.profile.apiUrl;
			const apiUrlParams = [];
			if (user?.avatar?.id) {
				apiUrlParams.push(`avatarId=${user.avatar.id}`);
			}
			if (user?.swid) {
				apiUrlParams.push("isUser=true");
			}
			if (makeProfileRequestForGettingTAData) {
				apiUrlParams.push(
					`travelAgentHeader=${encodeURIComponent(travelAgentHeader) || ""}`
				);
				apiUrlParams.push(`store=${store}`);
			}
			if (additionalProfileInfo) {
				apiUrlParams.push("additionalProfileInfo=true");
			}
			if (oneIdConfig?.asyncToken) {
				let generateToken = true;
				if (asyncMessagingToken) {
					const validToken = validateJsonWebToken(asyncMessagingToken);
					if (validToken && validToken.sub === user?.swid) {
						generateToken = false;
					}
				}
				if (generateToken) {
					setAsyncMessagingToken(null);
					apiUrlParams.push("asyncToken=true");
				}
			}
			if (notificationsType === "count") {
				apiUrlParams.push("useNotificationsCount=true");
			} else if (notificationsType === "heavy") {
				try {
					if (
						!loadHeavyNotificationsObject?.updatedAt ||
						loadHeavyNotificationsObject?.outdated ||
						new Date().getTime() - loadHeavyNotificationsObject?.updatedAt >
							loadHeavyNotificationsReloadTime
					) {
						setLoadHeavyNotificationsObject(null);
						apiUrlParams.push("loadHeavyNotifications=true");
					}
				} catch (error) {
					setLoadHeavyNotificationsObject(null);
					console.error(error);
				}
			}
			setProfileUrl(
				apiUrlParams.length ? `${apiUrl}?${apiUrlParams.join("&")}` : apiUrl
			);
		} else if (!user?.swid) {
			setProfileUrl("");
		}
		if (!makeProfileRequestForGettingTAData) {
			readDataFromCookie();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, brand, affiliationsParam]);

	useEffect(() => {
		isConversationStartedRef.current = isConversationStarted;
		closeChatRef.current = closeChat;
	}, [closeChat, isConversationStarted]);

	/**
	 * The `getNotifications` function processes and returns a structured object containing notification details used for
	 * 												the NotificationsModalContent, MessageCountBadge components and Context
	 *
	 * @param {Object} notifications - The notifications information fetched from the profile API.
	 * @param {boolean} isContext - A flag indicating whether to include the actual messages in the returned object.
	 *                              This flag is essential to avoid storing sensitive information, such as messages,
	 *                              in sessionStorage. Instead, when `isContext` is true, the function stores
	 *                              these values in the context while setting user data.
	 */
	const getNotifications = (notifications, isContext) => {
		let notificationMessages = {
			itemCount: 0,
			receivedInvitations: { hasNotifications: false, messages: [] },
			potentialDuplicates: { hasNotifications: false, messages: [] },
			mergeRequests: { hasNotifications: false, messages: [] },
		};

		if (notifications) {
			if (loadHeavyNotificationsObject) {
				notificationMessages.itemCount = loadHeavyNotificationsObject.itemCount;
			} else if (notifications?.itemCount) {
				notificationMessages.itemCount = notifications?.itemCount;
			}

			if (
				notifications?.receivedInvitations?.length > 0 ||
				loadHeavyNotificationsObject?.messages?.receivedInvitations
					?.hasNotifications
			) {
				notificationMessages.receivedInvitations = {
					hasNotifications: true,
					messages: isContext ? notifications.receivedInvitations : [],
				};
			}

			if (
				notifications?.potentialDuplicates?.length > 0 ||
				loadHeavyNotificationsObject?.messages?.potentialDuplicates
					?.hasNotifications
			) {
				notificationMessages.potentialDuplicates = {
					hasNotifications: true,
					messages: isContext ? notifications.potentialDuplicates : [],
				};
			}

			if (
				notifications.mergeRequests?.length > 0 ||
				loadHeavyNotificationsObject?.messages?.mergeRequests?.hasNotifications
			) {
				notificationMessages.mergeRequests = {
					hasNotifications: true,
					messages: isContext ? notifications.mergeRequests : [],
				};
			}
		}

		return notificationMessages;
	};

	const removeCartCookie = () => {
		const cartCookieToRemove = `CART-${brand}${store ? `_${store}` : ""}_jar`;
		if (cartCookieToRemove) {
			removeCookie(cartCookieToRemove.split("=")[0]);
		}
	};

	const loadCurrentUser = async () => {
		try {
			const userIsLoggedIn = await disneyIdRef.current.getLoggedInStatus();
			if (userIsLoggedIn) {
				const userData = await disneyIdRef.current.getGuest();
				const subscribed = isAllMarketingSubscribed(
					userData?.marketing,
					oneIdConfig?.marketingCodes
				);
				userData?.profile &&
					setUser({
						...user,
						...userData.profile,
						marketing: userData.marketing,
						authToken: userData.token?.access_token,
						subscribed,
					});
			}
		} catch (error) {
			console.error(error);
		}
	};

	const initializeProfileAuth = async () => {
		if (!window?.PEP.PROFILE_AUTH.did) {
			const init = await window?.PEP.PROFILE_AUTH.init({
				oneIdScriptSrc: oneIdConfig?.oneIdLibraryUrl,
				loadScript: true,
				clientId: oneIdConfig?.oneIdClientId,
				langPref: locale,
				responderPage: oneIdConfig?.oneIdResponderPage,
				cssOverride: oneIdConfig?.cssOverride,
			});
			if (!init) {
				publishOneIdError({
					message: "One Id init event didn't return a true value",
				});
			}
		}
		disneyIdRef.current = window?.PEP.PROFILE_AUTH;
	};

	const checkWindowDid = async () => {
		return new Promise((resolve) => {
			let checks = 0;
			const checkOneID = () => {
				if (window?.did) {
					disneyIdRef.current = window?.did;
					resolve();
				} else if (checks >= maxChecks) {
					resolve();
				} else {
					checks++;
					setTimeout(checkOneID, timeoutMs);
				}
			};
			checkOneID();
		});
	};

	const checkWindowOneID = async () => {
		return new Promise((resolve) => {
			let checks = 0;
			const checkOneID = () => {
				try {
					const oneID = window?.OneID?.get();
					if (oneID) {
						disneyIdRef.current = oneID;
						resolve();
					} else if (checks >= maxChecks) {
						resolve();
					} else {
						checks++;
						setTimeout(checkOneID, timeoutMs);
					}
				} catch (error) {
					checks++;
					setTimeout(checkOneID, timeoutMs);
				}
			};
			checkOneID();
		});
	};

	const checkOneIdCustomObject = async () => {
		if (oneIdConfig.checkForOneIdCustomObject) {
			await checkWindowDid();
		} else {
			await checkWindowOneID();
		}
	};

	const retryInitializeOneID = async () => {
		let initializeCurrentRef = true;
		if (!window?.OneID) {
			await new Promise((resolve) => {
				let checks = 0;
				const checkOneIDScript = () => {
					if (window?.OneID?.get) {
						disneyIdRef.current = window?.OneID.get();
						if (disneyIdRef.current) {
							initializeCurrentRef = false;
						}
						resolve();
					} else if (checks >= maxChecks) {
						resolve();
					} else {
						checks++;
						setTimeout(checkOneIDScript, timeoutMs);
					}
				};
				checkOneIDScript();
			});
		}

		if (initializeCurrentRef) {
			disneyIdRef.current = window?.OneID?.get({
				clientId: oneIdConfig?.oneIdClientId,
				responderPage: oneIdConfig?.oneIdResponderPage,
				cssOverride: oneIdConfig?.cssOverride,
			});
			await disneyIdRef?.current?.init();
		}
	};

	const initOneId = async () => {
		try {
			if (oneIdConfig?.usingOneIdProfileLibrary && window?.PEP?.PROFILE_AUTH) {
				await initializeProfileAuth();
			} else if (window?.OneID || localOneIdLibraryInstance) {
				if (
					!oneIdConfig.forceScriptLoadAndInitialization &&
					oneIdConfig.preventInitialization &&
					!isolatedEnvironment
				) {
					await checkOneIdCustomObject();
				}
				if (!disneyIdRef.current) {
					await retryInitializeOneID();
				}
			}
			if (disneyIdRef.current) {
				if (!disneyIdRef.current?.isInitialized) {
					publishOneIdError({ message: "One Id didn't initialize" });
				}
				await loadCurrentUser();
				initOneIdEvents();
				publishNavigationEvent(`${oneIdPrefix}init`);
				disneyIdRef.current.logoutPreventReloadAndRedirect =
					disneyIdRef.current.logoutPreventReloadAndRedirect ||
					oneIdConfig.preventPageReloadAndRedirect;
			} else {
				publishOneIdError({
					message: "One Id object is not defined on window",
				});
				console.error("Navigation App couldn't initialize One Id events");
			}
		} catch (error) {
			publishOneIdError(error);
			console.error(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRedirectAndReloadLogic = (redirectKey = "Login") => {
		const redirectUrl =
			disneyIdRef.current[`on${redirectKey}RedirectTo`] ||
			disneyIdRef.current[`on${redirectKey}RedirectUrl`];
		if (redirectUrl) {
			window?.location.assign(redirectUrl);
		} else {
			window?.location.reload();
		}
	};

	const initOneIdEvents = () => {
		disneyIdRef.current.on("login", (data) => {
			setLoadHeavyNotificationsObject(null);
			setAsyncMessagingToken(null);
			navigationAllowReloadAndRedirect = true;
			publishNavigationEvent(`${oneIdPrefix}login`, data);
			const pathPreventReload = isCurrentPathPresent(
				brandConfig?.excludeReloadPages
			);
			if (
				disneyIdRef.current.launchLoginPreventReloadAndRedirect ||
				disneyIdRef.current.launchReauthPreventReloadAndRedirect ||
				oneIdConfig.usingOneIdLibrary ||
				pathPreventReload
			) {
				navigationAllowReloadAndRedirect = false;
				const subscribed = isAllMarketingSubscribed(
					data?.marketing,
					oneIdConfig?.marketingCodes
				);

				data?.profile &&
					setUser({
						...data.profile,
						marketing: data.marketing,
						authToken: data.token?.access_token,
						subscribed,
					});
				publishNavigationEvent(`${oneIdPrefix}init`);
				if (disneyIdRef.current.leadGenLogin) {
					disneyIdRef.current.leadGenLoggedIn = true;
					handleSubscription();
				}
			} else if (
				isolatedEnvironment ||
				(navigationAllowReloadAndRedirect && oneIdConfig?.ignoreInitEvent)
			) {
				navigationAllowReloadAndRedirect = false;
				handleRedirectAndReloadLogic();
			}
		});

		disneyIdRef.current.on("session-init", () => {
			const pathPreventReload = isCurrentPathPresent(
				brandConfig?.excludeReloadPages
			);

			if (
				navigationAllowReloadAndRedirect &&
				!pathPreventReload &&
				!disneyIdRef.current.launchLoginPreventReloadAndRedirect &&
				!disneyIdRef.current.launchReauthPreventReloadAndRedirect
			) {
				handleRedirectAndReloadLogic();
			}
		});

		disneyIdRef.current.on("logout", async (data) => {
			setAsyncMessagingToken(null);
			setUser(null);
			setLoadHeavyNotificationsObject(null);
			if (isConversationStartedRef.current) {
				await closeChatRef?.current();
			}
			if (
				!disneyIdRef.current.logoutPreventReloadAndRedirect &&
				(!oneIdConfig.checkForAllowReloadAndRedirectInLogout ||
					(oneIdConfig.checkForAllowReloadAndRedirectInLogout &&
						navigationAllowReloadAndRedirect))
			) {
				handleRedirectAndReloadLogic("Logout");
			}
			publishNavigationEvent(`${oneIdPrefix}logout`);
		});

		disneyIdRef.current.on("update", (data) => {
			// data.profile contains the changed info
			const subscribed = isAllMarketingSubscribed(
				data?.marketing,
				oneIdConfig?.marketingCodes
			);
			data?.profile &&
				setUser({
					...user,
					...data.profile,
					marketing: data.marketing,
					subscribed,
				});
			publishNavigationEvent(`${oneIdPrefix}update`);
		});

		disneyIdRef.current.on("close", () => {
			setOpenedOneIdModal(false);
			publishNavigationEvent(`${oneIdPrefix}close`);
		});

		disneyIdRef.current.on("opt-in", () => {
			setShowSubscriptionMessage(true);
			publishNavigationEvent(`${oneIdPrefix}opt-in`);
		});

		["create", "reauth", "refresh", "low-trust"].forEach((event) => {
			disneyIdRef.current.on(event, () =>
				publishNavigationEvent(`${oneIdPrefix}${event}`)
			);
		});
	};

	const launchOneIdEvent = async (
		oneIdLibrary = {},
		method = "",
		argument = ""
	) => {
		if (oneIdLibrary) {
			try {
				await oneIdLibrary[method](argument);
				if (method?.indexOf("launch") === 0) {
					setOpenedOneIdModal(true);
				}
				return true;
			} catch (error) {
				console.error(error);
				return false;
			}
		} else {
			return false;
		}
	};

	const handleLogin = async (
		event,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false
	) => {
		if (!oneIdConfig.preventAuthenticationModals) {
			if (isOneIdSource) {
				handleClick(
					launchLoginMethod,
					onLoginRedirectTo,
					onLogoutRedirectTo,
					preventReloadAndRedirect,
					preventExecuteOneIdMethod
				)(event);
			} else {
				event?.preventDefault();
				if (disneyIdRef.current) {
					try {
						if (
							await launchOneIdEvent(disneyIdRef.current, launchLoginMethod)
						) {
							setOpenedOneIdModal(false);
							return;
						}
					} catch (error) {
						console.error(error);
					}
				}
				window?.location.assign(
					addCurrentRedirectUrl(
						buildHref(
							chromeData?.signIn?.href || "/login/",
							store,
							basePath,
							false
						),
						true
					)
				);
			}
		}
	};

	const handleLogout = (
		event,
		onLogoutRedirectTo = null,
		isEventSource = false,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false
	) => {
		navigationAllowReloadAndRedirect = true;
		setSyndicatedDismissButton(false);
		setStorageMakeCartRequest(true);
		removeCartCookie();
		if (
			disneyIdRef.current?.forceEnablePageReloadAndRedirect &&
			disneyIdRef.current?.onLogoutRedirectTo
		) {
			disneyIdRef.current.logoutPreventReloadAndRedirect = false;
		}
		if (isOneIdSource && !isTravelAgent(userType)) {
			disneyIdRef.current.onLogoutRedirectUrl = addCurrentRedirectUrl(
				chromeData?.signOut?.href,
				false
			);
			handleClick(
				"logout",
				null,
				onLogoutRedirectTo,
				preventReloadAndRedirect,
				preventExecuteOneIdMethod
			)(event);
		} else if (isEventSource && onLogoutRedirectTo) {
			window?.location.assign(onLogoutRedirectTo);
		} else if (
			disneyIdRef.current?.onLogoutRedirectTo &&
			event?.currentTarget?.href
		) {
			event.currentTarget.href = disneyIdRef.current.onLogoutRedirectTo;
		} else if (window?.navigationKeepInFlow && event?.currentTarget?.href) {
			event.currentTarget.href = addCurrentRedirectUrl(
				event.currentTarget.href,
				true,
				true
			);
		}
	};

	const handleProfile = (
		event,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		isEventSource = false
	) => {
		if (chromeData?.signedIn) {
			if (
				isOneIdSource &&
				(chromeData.signedIn?.target === config.targetOneId || bannerProfile)
			) {
				handleClick(
					"launchProfile",
					onLoginRedirectTo,
					onLogoutRedirectTo
				)(event);
			} else if (chromeData.signedIn?.href && isEventSource) {
				window?.location.assign(chromeData.signedIn.href);
			}
		}
	};

	const handleSubscription = async (event = null) => {
		if (!user && !disneyIdRef.current.leadGenLoggedIn) {
			disneyIdRef.current.leadGenLogin = true;
			handleLogin(event, null, null, true);
		} else {
			event?.preventDefault();
			try {
				await launchOneIdEvent(
					disneyIdRef.current,
					"launchNewsletters",
					oneIdConfig?.promotionId
				);
			} catch (error) {
				console.error(error);
			}
		}
	};

	useEffect(() => {
		if (triggerNotificationCount !== 0) {
			if (notificationsType === "heavy") {
				const storedNotificationsCount = { ...loadHeavyNotificationsObject };
				if (user?.cookieBasedNotifications) {
					if (notificationCount) {
						setLoadHeavyNotificationsObject({
							...storedNotificationsCount,
							itemCount: storedNotificationsCount.itemCount - 1,
							updatedAt: new Date().getTime(),
							outdated: false,
						});
						setNotificationCount(notificationCount - 1);
					}
				} else {
					setLoadHeavyNotificationsObject({
						...storedNotificationsCount,
						itemCount: storedNotificationsCount.itemCount - 1,
						outdated: true,
					});
				}
			}

			if (user?.messages?.itemCount > 0) {
				const updatedUser = JSON.parse(JSON.stringify(user));
				updatedUser.messages.itemCount--;
				setUser(updatedUser);
			}
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerNotificationCount]);

	const handleReadNotification = () => {
		setTriggerNotificationCount(
			triggerNotificationCount + new Date().getTime()
		);
	};

	const handleClick = (
		method,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false,
		argument = null
	) => {
		return async (e) => {
			setAsyncMessagingToken(null);
			if (
				isLoading ||
				!isOneIdSource ||
				!disneyIdRef.current ||
				!disneyIdRef.current[method]
			)
				return;
			e?.preventDefault();
			if (onLoginRedirectTo) {
				disneyIdRef.current.onLoginRedirectTo = onLoginRedirectTo;
			}
			if (onLogoutRedirectTo) {
				disneyIdRef.current.onLogoutRedirectTo = onLogoutRedirectTo;
			}
			if (preventReloadAndRedirect) {
				disneyIdRef.current[`${method}PreventReloadAndRedirect`] =
					preventReloadAndRedirect;
			}
			if (!preventExecuteOneIdMethod) {
				launchOneIdEvent(disneyIdRef.current, method, argument);
			}
		};
	};

	return {
		isLoading,
		user,
		isOneIdSource,
		setUser,
		userType,
		setUserType,
		travelAgent,
		setTravelAgent,
		handleClick,
		openedOneIdModal,
		affiliations,
		handleLogin,
		handleLogout,
		handleProfile,
		handleSubscription,
		showSubscriptionMessage,
	};
};

export default useProfile;
